import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { ApiQueryParams } from '@/api/Api';
import {
  listWebsiteModules,
  type GetWebsiteModuleListRequest,
  type GetWebsiteModuleListResponse,
} from '@/api/module/list';
import {
  readWebsiteModule,
  type ReadWebsiteModuleResponse,
} from '@/api/module/read';
import {
  createWebsiteModule,
  type CreateWebsiteModuleResponse,
} from '@/api/module/create';
import {
  updateWebsiteModule,
  type UpdateWebsiteModuleResponse,
} from '@/api/module/update';
import {
  deleteWebsiteModule,
  type DeleteWebsiteModuleRequest,
  type DeleteWebsiteModuleResponse,
} from '@/api/module/delete';
import {
  cloneWebsiteModule,
  type CloneWebsiteModuleResponse,
} from '@/api/module/clone';
import {
  importWebsiteModules,
  type ImportWebsiteModuleRequest,
  type ImportWebsiteModuleResponse,
} from '@/api/module/import';
import {
  exportWebsiteModules,
  type ExportWebsiteModuleRequest,
  type ExportWebsiteModuleResponse,
} from '@/api/module/export';
import type { WebsiteModule } from '@/interfaces/module.interface';
import {
  buildParams,
  mergeApiQueryParams,
  type FilterParam,
} from './utils';
import { normalizeOid } from '@/utils/normalizeOid';
import { prepareModulePayloadForApi } from '@/utils/moduleMapper';
import { notifyApiError, shouldSuppressErrorToast } from './error';

const MODULE_LIST_KEY = 'modules';
const MODULE_ITEM_KEY = 'module';

type ModulesQueryOptions = Omit<
  UseQueryOptions<
    GetWebsiteModuleListResponse,
    unknown,
    GetWebsiteModuleListResponse,
    [typeof MODULE_LIST_KEY, GetWebsiteModuleListRequest]
  >,
  'queryKey' | 'queryFn'
>;

export const useModules = (
  params?: Partial<GetWebsiteModuleListRequest>,
  options?: ModulesQueryOptions
) => {
  // Extract filters from params (they will be handled separately)
  const { filters, ...paramsWithoutFilters } = params ?? {};
  
  // Exclude standard API query params from paramsWithoutFilters to prevent overwriting env vars
  // Standard params (project_id, collection_key, etc.) should come from env vars via mergeApiQueryParams
  const {
    project_id,
    collection_key,
    brand_service_id,
    ...nonStandardParams
  } = paramsWithoutFilters;
  
  const finalParams: GetWebsiteModuleListRequest = {
    page: params?.page ?? 1,
    page_limit: params?.page_limit ?? 50,
    ...mergeApiQueryParams<GetWebsiteModuleListRequest>(
      'module',
      undefined, // Don't pass any overrides - use env vars only
      'list'
    ),
    ...nonStandardParams, // Only spread non-standard params (page, page_limit, sort_by, etc.)
    // Add filters back (will be converted to fk[index][field] format in API call)
    ...(filters ? { filters } : {}),
  };

  const query = useQuery<GetWebsiteModuleListResponse, unknown>({
    queryKey: [MODULE_LIST_KEY, finalParams],
    queryFn: async () => {
      const response = await listWebsiteModules(finalParams);
      const body = response.data;

      if (body && Array.isArray(body.data)) {
        return {
          ...body,
          data: body.data.map(
            (item) => normalizeOid(item) as WebsiteModule,
          ),
        };
      }

      return body;
    },
    ...options,
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'module', action: 'list' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchWebsiteModules = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (params?: Partial<GetWebsiteModuleListRequest>) => {
      const { filters, ...paramsWithoutFilters } = params ?? {};
      
      // Exclude standard API query params from paramsWithoutFilters to prevent overwriting env vars
      const {
        project_id,
        collection_key,
        brand_service_id,
        ...nonStandardParams
      } = paramsWithoutFilters;
      
      const finalParams: GetWebsiteModuleListRequest = {
        page: params?.page ?? 1,
        page_limit: params?.page_limit ?? 50,
        ...mergeApiQueryParams<GetWebsiteModuleListRequest>(
          'module',
          undefined, // Don't pass any overrides - use env vars only
          'list'
        ),
        ...nonStandardParams, // Only spread non-standard params
        ...(filters ? { filters } : {}),
      };

      try {
        await queryClient.prefetchQuery({
          queryKey: [MODULE_LIST_KEY, finalParams],
          queryFn: async () => {
            const response = await listWebsiteModules(finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch modules failed', error);
        }
      }
    },
    [queryClient]
  );
};

type ModuleQueryOptions = Omit<
  UseQueryOptions<
    ReadWebsiteModuleResponse,
    unknown,
    ReadWebsiteModuleResponse,
    [typeof MODULE_ITEM_KEY, string | undefined, ApiQueryParams]
  >,
  'queryKey' | 'queryFn'
>;

export const useModule = (
  moduleId?: string,
  params?: Partial<ApiQueryParams>,
  options?: ModuleQueryOptions
) => {
  const finalParams = buildParams('module', params, 'read');

  const query = useQuery<ReadWebsiteModuleResponse, unknown>({
    queryKey: [MODULE_ITEM_KEY, moduleId, finalParams],
    queryFn: async () => {
      if (!moduleId) {
        throw new Error('moduleId is required to fetch module details');
      }
      const response = await readWebsiteModule(moduleId, finalParams);
      const body = response.data;

      if (body?.data) {
        return {
          ...body,
          data: normalizeOid(body.data) as WebsiteModule,
        };
      }

      return body;
    },
    ...options,
    enabled: Boolean(moduleId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'module', action: 'read' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchWebsiteModule = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (moduleId?: string, params?: Partial<ApiQueryParams>) => {
      if (!moduleId) return;

      const finalParams = buildParams('module', params, 'read');

      try {
        await queryClient.prefetchQuery({
          queryKey: [MODULE_ITEM_KEY, moduleId, finalParams],
          queryFn: async () => {
            const response = await readWebsiteModule(moduleId, finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch module failed', error);
        }
      }
    },
    [queryClient]
  );
};

type CreateModuleVariables = {
  data: WebsiteModule;
  params?: Partial<ApiQueryParams>;
};

type CreateModuleOptions = UseMutationOptions<
  CreateWebsiteModuleResponse,
  unknown,
  CreateModuleVariables
>;

export const useCreateModule = (
  options?: CreateModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CreateWebsiteModuleResponse, unknown, CreateModuleVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('module', params, 'create');
      const response = await createWebsiteModule(
        finalParams,
        prepareModulePayloadForApi(
          normalizeOid(data) as Record<string, unknown>,
        ) as WebsiteModule,
      );
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'create' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [MODULE_LIST_KEY] });
      const createdId = variables.data?.id ?? data?.data?.id;
      if (createdId) {
        queryClient.invalidateQueries({
          queryKey: [MODULE_ITEM_KEY, createdId],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type UpdateModuleVariables = {
  id: string;
  data: Partial<WebsiteModule>;
  params?: Partial<ApiQueryParams>;
};

type UpdateModuleOptions = UseMutationOptions<
  UpdateWebsiteModuleResponse,
  unknown,
  UpdateModuleVariables
>;

export const useUpdateModule = (
  options?: UpdateModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<UpdateWebsiteModuleResponse, unknown, UpdateModuleVariables>({
    mutationFn: async ({ id, data, params }) => {
      const finalParams = buildParams('module', params, 'update');
      const response = await updateWebsiteModule(
        id,
        finalParams,
        prepareModulePayloadForApi(
          normalizeOid(data) as Record<string, unknown>,
        ) as Partial<WebsiteModule>,
      );
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'update' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [MODULE_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [MODULE_ITEM_KEY, variables.id],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type DeleteModuleVariables = {
  id: string;
  params?: Partial<DeleteWebsiteModuleRequest>;
};

type DeleteModuleOptions = UseMutationOptions<
  DeleteWebsiteModuleResponse,
  unknown,
  DeleteModuleVariables
>;

export const useDeleteModule = (
  options?: DeleteModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<DeleteWebsiteModuleResponse, unknown, DeleteModuleVariables>({
    mutationFn: async ({ id, params }) => {
      const finalParams = mergeApiQueryParams<DeleteWebsiteModuleRequest>(
        'module',
        params ?? {},
        'delete'
      );
      const response = await deleteWebsiteModule(id, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'delete' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [MODULE_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [MODULE_ITEM_KEY, variables.id],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type CloneModuleVariables = {
  id: string;
  params?: Partial<ApiQueryParams>;
  data?: Partial<WebsiteModule>;
};

type CloneModuleOptions = UseMutationOptions<
  CloneWebsiteModuleResponse,
  unknown,
  CloneModuleVariables
>;

export const useCloneModule = (
  options?: CloneModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CloneWebsiteModuleResponse, unknown, CloneModuleVariables>({
    mutationFn: async ({ id, params, data }) => {
      const finalParams = buildParams('module', params, 'clone');
      const response = await cloneWebsiteModule(id, finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'clone' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [MODULE_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ImportModuleVariables = {
  data: unknown;
  params?: Partial<ImportWebsiteModuleRequest>;
};

type ImportModuleOptions = UseMutationOptions<
  ImportWebsiteModuleResponse,
  unknown,
  ImportModuleVariables
>;

export const useImportModule = (
  options?: ImportModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ImportWebsiteModuleResponse, unknown, ImportModuleVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = mergeApiQueryParams<ImportWebsiteModuleRequest>(
        'module',
        params ?? {},
        'import' // Use 'list' service type since import filters like list endpoint
      );
      const response = await importWebsiteModules(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'import' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [MODULE_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ExportModuleVariables = {
  params?: Partial<ExportWebsiteModuleRequest>;
};

type ExportModuleOptions = UseMutationOptions<
  ExportWebsiteModuleResponse,
  unknown,
  ExportModuleVariables
>;

export const useExportModule = (
  options?: ExportModuleOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ExportWebsiteModuleResponse, unknown, ExportModuleVariables>({
    mutationFn: async ({ params }) => {
      // Extract filters to preserve them (buildParams doesn't include filters)
      const { filters, ...paramsWithoutFilters } = params ?? {};
      const baseParams = buildParams('module', paramsWithoutFilters, 'export');
      // Add filters back to the params
      const finalParams = {
        ...baseParams,
        ...(filters ? { filters } : {}),
      };
      const response = await exportWebsiteModules(finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'module', action: 'export' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
};

